import { useQuery } from "@tanstack/react-query";
import { axios } from "./axios";
import { useAuth } from "../context/AuthContext";
import { env } from "../utils/env";
import { IndividualListItem } from "../types/Individual";
import { DateTime } from "luxon";
import { PagedResponse } from "../types/Common";
import { useNavigate } from "react-router-dom";
import { ROUTE } from "../routes";

const fetch = async (
  authHeader: string,
  users?: number[],
  searchTerm?: string,
  filters?: any,
  page?: number,
  page_size?: number,
  sort_by?: string,
  order_by?: string,
  checkType?: string
) => {
  const url = new URL(
    `${env.verifyServiceUrl}individual/individual/?mandate__assigned_user=${users}&mandate__category=Checks`
  );


  // Both generations of screening check stay in this filter for every tenant,
  // because a migrated tenant holds records of both kinds. Leaving the ln_*
  // names out hides LexisNexis2 records from the Screening list entirely.
  if (checkType === "entity") {
    const checkTypes =
      filters.checkType ||
      "address_verification_check,entity_adverse_media_check,sanctions_check,ln_screening_check_entity";
    url.searchParams.set("checks__in", checkTypes);

  } else if(checkType === "individual") {
    const checkTypes =
      filters.checkType ||
      "address_verification_check,adverse_media_check,pep_&_sanctions_check,ln_screening_check";
    url.searchParams.set("checks__in", checkTypes);
  } else {
    const checkTypes =
      filters.checkType ||
      "address_verification_check,adverse_media_check,entity_adverse_media_check,pep_&_sanctions_check,sanctions_check,ln_screening_check,ln_screening_check_entity";
    url.searchParams.set("checks__in", checkTypes);
  }

  if (filters.dateStarted?.[0]) {
    url.searchParams.set(
      "created_on_after",
      DateTime.fromJSDate(filters.dateStarted[0]).toFormat("yyyy-MM-dd")
    );
  }

  if (filters.dateStarted?.[1]) {
    url.searchParams.set(
      "created_on_before",
      DateTime.fromJSDate(filters.dateStarted[1]).toFormat("yyyy-MM-dd")
    );
  }

  if (searchTerm?.length > 0) {
    url.searchParams.set("search", searchTerm);
  }

  if (page) {
    url.searchParams.set("page", page.toString());
  }

  if (Number.isInteger(page_size)) {
    url.searchParams.set("page_size", page_size.toString());
  }

  if (sort_by && order_by) {
    const prefix = sort_by === "dsc" ? "-" : "";
    url.searchParams.set("ordering", `${prefix}${order_by}`);
  }

  if (checkType) {
    url.searchParams.set("_type", checkType.toString());
  }

  return axios.get<PagedResponse<IndividualListItem[]>>(url.href, {
    headers: {
      Authorization: authHeader,
    },
  });
};

export const useIndividualsList = (
  users?: number[],
  searchTerm?: string,
  filters?: any,
  page?: number,
  page_size?: number,
  sort_by?: string,
  order_by?: string,
  checkType?: string
) => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  return useQuery(
    [
      "individualList",
      users,
      searchTerm,
      filters,
      page,
      page_size,
      sort_by,
      order_by,
      checkType,
    ],
    () =>
      fetch(
        authHeader,
        users,
        searchTerm,
        filters,
        page,
        page_size,
        sort_by,
        order_by,
        checkType
      ),
    {
      keepPreviousData: true,
      retry: true,
      onError: (err: any) => {
        if (err.request.status === 500 || err.request.status === 503) {
          navigate(ROUTE.SERVER_ERROR);
        }
      },
    }
  );
};
